// The page shell: [topbar: menu | status links | labels] [board] [footer] [ad band], the window
// host and the keyboard shortcuts. Legacy ids and classes are kept where the e2e suite reads them
// (.ai/rewrite-notes.md 3.7).
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { env } from '../env';
import { sharpFlag } from '../options/assets';
import { lastSound } from '../sound/sound';
import { Board } from '../board/Board';
import { AdAnchorBand } from './AdAnchorBand';
import { Indicator } from './Indicator';
import type { GameId } from '../engine/types';
import { currentLanguage, t } from '../i18n';
import { useController, useEngineState } from '../store/gameStore';
import { uiStore, useUiState, type WindowInstance } from '../store/uiStore';
import { GamesWindow } from '../windows/GamesWindow';
import { OptionsWindow } from '../windows/OptionsWindow';
import { AlertWindow, ConfirmWindow, InfoWindow, NoMovesWindow, PauseWindow } from '../windows/SimpleWindows';
import { RULES } from '../engine/rules';
import { showHint } from '../ui/mahjongHelp';
import { openRules } from '../rules/rulesUrl';
import { effectsOnFor, oldBrowser } from '../ui/effects';
import { ThemesWindow } from '../windows/ThemesWindow';
import { AuthWindow } from '../windows/AuthWindow';
import { DashboardWindow, DeleteAccountWindow, FlagsWindow, RankingWindow } from '../windows/AccountWindows';
import { WinWindow } from '../windows/WinWindow';
import { mpStore, useMultiplayerState } from '../multiplayer/store';
import { MpSearching } from '../windows/MultiplayerLobby';
import { fetchWaiting } from '../multiplayer/waiting';
import { CardNotice } from '../online/CardNotice';
import {
  MultiplayerDoneWindow,
  MultiplayerLeaveWindow,
  MultiplayerResultWindow,
  MultiplayerWindow,
  OpponentBar,
} from '../windows/MultiplayerWindows';
import { onMediaChange } from '../ui/compat';
import { HeartsScoreWindow, HeartsTable } from '../hearts/HeartsTable';
import { useHearts } from '../hearts/store';
import { MakaoEndWindow, MakaoTable } from '../makao/MakaoTable';
import { useMakao } from '../makao/store';
import { winScore } from '../makao/rules';
import { KulkiEndWindow, KulkiTable } from '../kulki/KulkiTable';
import { kulkiStore, useKulki } from '../kulki/store';
import { ScoreWindow358, Table358 } from '../358/Table358';
import { use358 } from '../358/store';
import { ScoreWindow1000, Table1000 } from '../1000/Table1000';
import { use1000 } from '../1000/store';
import type { SpiderColour } from '../options/spiderColour';

const pad = (n: number) => String(n).padStart(2, '0');
/** `mm:ss`; minutes keep counting past 59 (legacy wrapped to 00:00 at one hour, Q2). */
export const formatTime = (seconds: number) => `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;

export const isMobileBody = () => document.body.id === 'mobile';
const MENU_AUTO_HIDE_MS = 3000;

/** Decks that have `_v1` / `_v2` variants (footer card-variant toggle, themes.js:111-118). */
const VARIANT_DECKS = ['classic_old', 'modern', 'classic', 'strips'];
export const deckBase = (deck: string) => deck.replace(/_v[12]$/, '');

export interface ShellActions {
  /** `layout`: Mahjong's shape (null: a random one), 3-5-8's length or Tysiąc's target picked in the Games window
   *  (undefined: no such choice); `shapeBefore`: Mahjong's saved shape when the window opened (the window saves a pick
   *  at once); `colourBefore`: Spider one suit's saved colour when the window opened (null random, undefined: not
   *  given). */
  play(game: GameId, layout?: number | null, shapeBefore?: number | null, colourBefore?: SpiderColour): void;
  togglePause(): void;
  resume(): void;
}

const MENU_SLIDE_MS = 200;
/** Where the menu is a sheet over the top of the table (app.css: below 840 px, where the pill row would wrap). */
const SHEET_TIER = '(max-width: 839px), (max-height: 500px)'; // app.css: the menu as a sheet
/** Short screens (a phone held sideways, a small window; owner 2026-10-23 after a 344x286 report): the
 *  bottom dock is hidden but for Privacy (app.css, end of file) and its other buttons move into the Menu, so
 *  the cards get that height. */
export const SHORT_TIER = '(max-height: 500px)';

function useMedia(query: string): boolean {
  return useSyncExternalStore(
    (notify) => {
      const m = matchMedia(query);
      return onMediaChange(m, notify);
    },
    () => matchMedia(query).matches,
  );
}

/** The dock's actions, shared by the footer and (on short screens) the Menu. */
function useDockActions(actions: ShellActions) {
  const ui = useUiState();
  const ctrl = useController();
  const consentUi = useConsentUi();
  const mobile = isMobileBody();
  const sound = ui.options.option.sound;
  const deck = ui.options.skin.deckType;
  // The Cards button's 4 dots (owner 2026-10-23): 1-3 are the deck's three variants with the 3D effects on, 4 is
  // the Classic mode (the effects off) with variant 1 on a desktop, the big-sign variant 3 on phones. A deck picked
  // in the Themes window with the effects off shows its own dot.
  const effectsOn = effectsOnFor(ui.options.option.effects);
  const base = deckBase(deck);
  const classicDeck = mobile ? `${base}_v2` : base;
  // an old browser has the classic mode for good (ui/effects.ts oldBrowser): the button only steps the deck 1 -> 2 -> 3
  const locked = oldBrowser();
  const variant =
    !locked && !effectsOn && deck === classicDeck ? '4' : deck === base ? '1' : deck.endsWith('_v1') ? '2' : '3';
  return {
    sound,
    deck,
    variant,
    consentUi,
    paused: uiStore().isOpen('pause'),
    hasVariants: VARIANT_DECKS.includes(deckBase(deck)),
    fullscreenOk: !mobile && typeof document.fullscreenEnabled === 'boolean' && document.fullscreenEnabled,
    toggleSound: () => uiStore().setOption('sound', sound === 'off' ? lastSound() : 'off'),
    togglePause: actions.togglePause,
    cycleVariant: () => {
      const next =
        variant === '1'
          ? `${base}_v1`
          : variant === '2'
            ? `${base}_v2`
            : variant === '3' && !locked
              ? classicDeck
              : base;
      uiStore().setSkin({ deckType: next });
      if (locked) return;
      // 3 -> 4 turns the effects off; any other step turns them on (stored only when it changes something)
      const wantOn = variant !== '3';
      if (wantOn !== effectsOn) uiStore().setOption('effects', wantOn ? 'on' : 'off');
    },
    toggleFullscreen: () => {
      if (document.fullscreenElement) void document.exitFullscreen();
      else void document.documentElement.requestFullscreen?.();
    },
    rules: () => openRules(ctrl.getState().game),
    // Mahjong: the hint (ui/mahjongHelp.ts); no other game has one
    tiles: RULES[useEngineState().game].tiles === true,
    hint: () => showHint(ctrl),
    privacy: () => tcfApi()?.('displayConsentUi', 2, () => undefined),
  };
}
let menuSliding = false;
let menuSlideFallback: number | null = null;
const UNDO_KEY_THROTTLE_MS = 500;
let lastUndoKeyAt = 0;
let menuHideTimer: number | null = null;
/** The 3 s auto-hide (legacy menu.js, MENU-013). It never closes a menu the player is using (owner 2026-10-18:
 *  "menu się chowa", as they went to click New game): opening the menu with its button cancels it, and a mouse
 *  over the menu puts it off by another 3 s (a mouse only: a tap leaves :hover stuck on touch screens). */
export function armMenuAutoHide() {
  cancelMenuAutoHide();
  menuHideTimer = window.setTimeout(() => {
    menuHideTimer = null;
    if (!uiStore().getState().menuOpen) return;
    if (matchMedia('(hover: hover)').matches && document.querySelector('#menu_content:hover, #menu_button:hover')) {
      armMenuAutoHide();
      return;
    }
    uiStore().set({ menuOpen: false });
  }, MENU_AUTO_HIDE_MS);
}

export function cancelMenuAutoHide() {
  if (menuHideTimer !== null) clearTimeout(menuHideTimer);
  menuHideTimer = null;
}

/** The row's entries are 100 px pills: a label wider than its pill (Georgian "აირჩიეთ თამაში", Welsh "Ail ddechrau'r
 *  gêm"; owner 2026-10-29: "Agree" to fitting them) gets a smaller font, just enough, never under 10 px. A label that
 *  fits is left alone; the phone sheet's entries have room and are never touched. */
const MENU_FONT_MIN = 10;
function useFitMenuLabels(key: string) {
  useLayoutEffect(() => {
    const fit = () => {
      for (const a of document.querySelectorAll<HTMLElement>('#menu_content > a.menu_action')) {
        a.style.fontSize = '';
        const cs = getComputedStyle(a);
        const room = a.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
        const range = document.createRange();
        range.selectNodeContents(a);
        const text = range.getBoundingClientRect().width;
        if (room <= 0 || text <= room + 0.5) continue;
        const size = parseFloat(cs.fontSize);
        a.style.fontSize = `${Math.max(MENU_FONT_MIN, Math.floor(((size * room) / text) * 10) / 10)}px`;
      }
    };
    // the Menu button too: a long word (Welsh "Dewislen", Japanese "メニュー" in its pill, the exit text during a game)
    // must not push into the row's first entry
    const fitButton = () => {
      const b = document.getElementById('menu_button');
      const first = document.querySelector<HTMLElement>('#menu_content > a.menu_action');
      if (!b) return;
      b.style.fontSize = '';
      if (!first || (getComputedStyle(first).position === 'static' && first.offsetParent === null)) return;
      const over = b.getBoundingClientRect().right - first.getBoundingClientRect().left;
      if (over <= 0) return;
      const size = parseFloat(getComputedStyle(b).fontSize);
      const range = document.createRange();
      range.selectNodeContents(b);
      const text = range.getBoundingClientRect().width;
      if (text > 0)
        b.style.fontSize = `${Math.max(MENU_FONT_MIN, Math.floor(((size * (text - over - 2)) / text) * 10) / 10)}px`;
    };
    const all = () => {
      fit();
      fitButton();
    };
    all();
    // the web font may come after the first measure
    void document.fonts?.ready.then(all);
    addEventListener('resize', all);
    return () => removeEventListener('resize', all);
  }, [key]);
}

/** A phone-width screen: during an online game the Menu button shows the door alone (the exit words did not fit its
 *  pill, owner 2026-10-29); the words are its aria-label. */
const NARROW_TIER = '(max-width: 559px)';

function Menu({ actions }: { actions: ShellActions }) {
  const short = useMedia(SHORT_TIER);
  const dock = useDockActions(actions);
  const ctrl = useController();
  const ui = useUiState();
  const mp = useMultiplayerState();
  const mobile = isMobileBody();
  const target = env.appTarget;
  const narrow = useMedia(NARROW_TIER);
  // during an online game the button leaves it: a door instead of the menu lines, the words on a wide screen, the door
  // alone on a phone (the words did not fit its pill)
  const inGame = !!(mp.gameStarted || mp.cards);
  // how many wait online, asked when the player opens the Menu (no timer; never told during a game, owner 2026-11-01)
  const [waiting, setWaiting] = useState(0);
  // "with people" under the label (owner 2026-11-01, pick C of branch prototype/mp-next-2026-11-01): on a site where
  // everything is online the words alone say little. In ::after, so the entry's name stays the label.
  const onlineLabel = <span className="menu_online" data-sub={t('menu.online-sub')}>{t('menuMultiplayerButton')}</span>;
  useEffect(() => {
    if (!ui.menuOpen || target === 'offline' || target === 'multiplayer' || inGame) return;
    let live = true;
    void fetchWaiting().then((list) => {
      if (live) setWaiting(list.length);
    });
    return () => {
      live = false;
    };
  }, [ui.menuOpen, target, inGame]);
  useFitMenuLabels(`${currentLanguage()}|${short}|${target}|${inGame}|${narrow}|${waiting}`);
  const onMenuButton = () => {
    if (mp.gameStarted || mp.cards) {
      // Leaving a running multiplayer game hands the win to the opponent: ask first, and offer "I'm done" (stop with
      // the score so far), what most of the players who left wanted (.ai/multiplayer-plan.md step 0b).
      uiStore().openWindow('multiplayer_leave');
      return;
    }
    // Clicks while the panel is still sliding are ignored (legacy menu.js). Tracked by the CSS
    // transition itself, not by a clock: the e2e tests freeze Date.now() and would lock the menu.
    if (menuSliding) return;
    menuSliding = true; // cleared by the panel's transitionend (or the fallback below)
    if (menuSlideFallback !== null) clearTimeout(menuSlideFallback);
    menuSlideFallback = window.setTimeout(() => {
      menuSliding = false;
    }, MENU_SLIDE_MS + 100);
    cancelMenuAutoHide(); // a timer armed before must not close the menu the player just opened (or reopen)
    uiStore().set({ menuOpen: !ui.menuOpen });
  };
  useEffect(() => {
    const panel = document.getElementById('menu_content');
    if (!panel) return;
    const end = (e: TransitionEvent) => {
      if (e.target === panel && (e.propertyName === 'top' || e.propertyName === 'transform')) menuSliding = false; // the phone sheet slides by a transform
    };
    panel.addEventListener('transitionend', end);
    panel.addEventListener('transitioncancel', end);
    return () => {
      panel.removeEventListener('transitionend', end);
      panel.removeEventListener('transitioncancel', end);
    };
  }, []);
  // data-icon names the legacy glyph image (basename without .png) the CSS mask replaces; the tests read it.
  const entry = (cls: string, label: ReactNode, onClick: () => void, ariaLabel?: string, icon?: string) => (
    <a
      href="#"
      className={`${cls} menu_action`}
      aria-label={ariaLabel}
      data-icon={icon}
      onClick={(e) => {
        e.preventDefault();
        onClick();
        // the phone sheet (and a touch device) closes at once on any entry (owner 2026-10-18): it covers
        // the top of the table and the window or the deal the entry opens
        if ((mobile || matchMedia(SHEET_TIER).matches) && uiStore().getState().menuOpen)
          uiStore().set({ menuOpen: false });
      }}
    >
      {label}
    </a>
  );
  /** An on/off entry (a switch, aria-checked) or, with `variant`, the card set's number (1-3). The menu stays open
   *  on a click, so the player sees the state change; Pause closes it (its window opens). */
  const toggle = (
    cls: string,
    label: ReactNode,
    onClick: () => void,
    ariaLabel: string,
    icon: string,
    on?: boolean,
    variant?: string,
    closes = false,
  ) => (
    <a
      href="#"
      className={`${cls} menu_action`}
      role={variant ? undefined : 'switch'}
      aria-checked={variant ? undefined : !!on}
      aria-label={ariaLabel}
      data-icon={icon}
      data-state={variant ? undefined : on ? 'on' : 'off'}
      data-variant={variant}
      onClick={(e) => {
        e.preventDefault();
        onClick();
        if (closes && uiStore().getState().menuOpen) uiStore().set({ menuOpen: false });
      }}
    >
      {label}
    </a>
  );
  const afterNewGame = () => {
    const s = uiStore();
    if (s.isOpen('options') || s.isOpen('opt_skin')) return;
    armMenuAutoHide();
  };
  return (
    <>
      <a
        id="menu_button"
        href="#"
        aria-expanded={ui.menuOpen}
        data-exit={inGame || undefined}
        aria-label={inGame && narrow ? t('menuExitMultiplayerButton') : undefined}
        onClick={(e) => {
          e.preventDefault();
          onMenuButton();
        }}
      >
        {inGame ? (narrow ? '' : t('menuExitMultiplayerButton')) : t('menuButton')}
      </a>
      <div id="menu_content" aria-hidden={!ui.menuOpen || undefined}>
        {target === 'multiplayer'
          ? null
          : entry('menu_new_game', t('menuNewGameButton'), () => {
              if (uiStore().isOpen('win')) uiStore().closeWindow('win');
              ctrl.newGame();
              afterNewGame();
            })}
        {target === 'multiplayer'
          ? null
          : entry('menu_restart_game', t('menuRestartGameButton'), () => {
              if (uiStore().isOpen('win')) uiStore().closeWindow('win');
              ctrl.retryGame();
              afterNewGame();
            })}
        {target === 'multiplayer'
          ? null
          : entry('menu_select_game', t('menuSelectGameButton'), () => uiStore().toggleWindow('games'))}
        {/* phones too since 2026-10-29 (owner: the full-screen lobby of protocol v2) */}
        {target === 'offline'
          ? null
          : entry(
              'menu_multiplayer',
              target === 'multiplayer'
                ? t('menuMultiplayerRoomsButton')
                : waiting > 0
                  ? (
                    <>
                      {onlineLabel}
                      <span className="menu_waiting" title={t('menu.online-waiting', { n: waiting })}>
                        <i aria-hidden="true" />
                        {waiting}
                      </span>
                    </>
                  )
                  : onlineLabel,
              () => {
                const w = uiStore()
                  .getState()
                  .windows.find((x) => x.name === 'multiplayer' && !x.closing);
                if (w && !w.minimized) {
                  uiStore().closeWindow('multiplayer');
                  mpStore().close();
                } else mpStore().open();
              },
            )}
        {entry('menu_option', t('menuOptionButton'), () => uiStore().toggleWindow('options'))}
        {entry('menu_skin', t('menuSkinButton'), () => uiStore().toggleWindow('opt_skin'))}
        {/* the ? entry: no text (the CSS draws attr(aria-label) next to the glyph) */}
        {target === 'offline'
          ? null
          : entry('menu_info', null, () => uiStore().toggleWindow('info'), t('infoHeader'), 'info')}
        {short ? (
          <>
            {/* the dock's entries on a low screen (owner 2026-10-23): Rules opens a window like the entries above;
                the on/off ones stand apart in their own group, each with its state (a switch; the card set's
                number), so they are not taken for entries that open a window */}
            {dock.tiles
              ? entry('menu_dock_hint', t('menuBottom.caption.hint'), dock.hint, t('menu-bottom.hint'), 'hint')
              : null}
            {entry('menu_dock_rules', t('menuBottom.caption.rules'), dock.rules, t('menu-bottom.rules'), 'bottom_info')}
            <div className="menu_toggles" role="group">
              {toggle(
                'menu_dock_sound',
                t('menuBottom.caption.sound'),
                dock.toggleSound,
                t('menu-bottom.sound'),
                dock.sound === 'off' ? 'sound_mute' : 'sound',
                dock.sound !== 'off',
              )}
              {toggle(
                'menu_dock_pause',
                t(dock.paused ? 'menuBottom.caption.paused' : 'menuBottom.caption.pause'),
                dock.togglePause,
                t('menu-bottom.pause'),
                'pause_off',
                dock.paused,
                undefined,
                true,
              )}
              {dock.hasVariants && !dock.tiles
                ? toggle(
                    'menu_dock_card_variant',
                    t('menu.cardVariant'),
                    dock.cycleVariant,
                    t('menu-bottom.card-variant'),
                    'card_variant',
                    undefined,
                    dock.variant,
                  )
                : null}
              {dock.fullscreenOk
                ? toggle(
                    'menu_dock_fullscreen',
                    t('menuBottom.caption.screen'),
                    dock.toggleFullscreen,
                    t('menu-bottom.fullscreen'),
                    'fullscreen_off',
                    ui.fullscreen,
                  )
                : null}
            </div>
          </>
        ) : null}
      </div>
      <span hidden>{String(actions.play.length)}</span>
    </>
  );
}

/**
 * "Time: 00:00" with the name in its own span: phones hide the name by CSS, the text stays whole.
 * The template decides the order ("{var} :الوقت" in Arabic puts the value first).
 */
function label(id: string, key: string, value: string | number, shown: boolean) {
  const template = t(key, { var: '\u0000' });
  const [before = '', after = ''] = template.split('\u0000');
  return (
    <span id={id} hidden={!shown || undefined}>
      {before ? <span className="label__prefix">{before}</span> : null}
      {String(value)}
      {after ? <span className="label__prefix">{after}</span> : null}
    </span>
  );
}

/** 560-1239 px (owner 2026-10-17, iPad portrait: "czy user powinien być na środku"; 2026-10-18: "powinien być
 *  centralnie dopóki nie mieści się z hudem"): See Rank / Login or the name pill sit packed after Undo in the
 *  flex row; this slides them (`--pair-shift`, a transform, app.css) to the page centre, or as near to it as the
 *  room before the HUD allows, keeping the Menu-Undo gap to the HUD (page-centred at 1240+ is plain CSS).
 *  The page width is the layout width the media queries use (`clientWidth`, `matchMedia`): iPad Safari gave an
 *  `innerWidth` that disagreed while its window was resized, and the pill fell back next to Undo. */
const PAIR_TIER = '(min-width: 560px) and (max-width: 1239px)';
function useCentredPair(ref: React.RefObject<HTMLDivElement | null>) {
  useLayoutEffect(() => {
    const bar = ref.current;
    if (!bar) return;
    const tier = matchMedia(PAIR_TIER);
    const place = () => {
      const hud = document.getElementById('game_labels');
      // the shown pills only (See Rank is display: none on phones)
      const pills = Array.from(bar.querySelectorAll<HTMLElement>('a:not(#game_action_undo)')).filter(
        (p) => p.getClientRects().length > 0,
      );
      const undo = bar.querySelector<HTMLElement>('#game_action_undo');
      const menu = document.getElementById('menu_button');
      if (!tier.matches || !pills.length || !undo) {
        bar.style.removeProperty('--pair-shift');
        return;
      }
      const vw = document.documentElement.clientWidth;
      const shift = parseFloat(bar.style.getPropertyValue('--pair-shift')) || 0;
      const rects = pills.map((p) => p.getBoundingClientRect());
      const left = Math.min(...rects.map((r) => r.left)) - shift;
      const right = Math.max(...rects.map((r) => r.right)) - shift;
      const gap = menu ? Math.max(0, undo.getBoundingClientRect().left - menu.getBoundingClientRect().right) : 10;
      const hudLeft = hud && !hud.hidden ? hud.getBoundingClientRect().left : vw - 16;
      const room = Math.max(0, hudLeft - gap - right); // the furthest it can go and keep the gap to the HUD
      const want = vw / 2 - (left + right) / 2;
      // the pair's left edge on a whole pixel (owner 2026-10-27: a light line at the join of See Rank and Login where
      // the two halves met between pixels; the pills before them have fractional widths)
      const target = Math.max(0, Math.min(want, room));
      const next = `${Math.round(left + target) - left}px`;
      if (bar.style.getPropertyValue('--pair-shift') !== next) bar.style.setProperty('--pair-shift', next);
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(bar);
    ro.observe(document.documentElement);
    const hud = document.getElementById('game_labels');
    if (hud) ro.observe(hud);
    // the pills change without the bar changing size: Login -> the name pill, the language, a late font
    const mo = new MutationObserver(place);
    mo.observe(bar, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    if (hud)
      mo.observe(hud, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class'] });
    // full screen and the no-distraction mode move Menu / Undo without resizing anything observed above
    const app = bar.closest('.app');
    if (app) mo.observe(app, { attributes: true, attributeFilter: ['data-fullscreen', 'data-focus'] });
    addEventListener('resize', place);
    window.visualViewport?.addEventListener('resize', place);
    const stopTier = onMediaChange(tier, place);
    void document.fonts?.ready.then(place);
    return () => {
      ro.disconnect();
      mo.disconnect();
      removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('resize', place);
      stopTier();
    };
  }, [ref]);
}

/** From 1240 px Undo sits at x 130 (the legacy place, app.css), but a wider Menu button covered it: "Leave multiplayer"
 *  ("Wyjdź z trybu multiplayer") during a multiplayer game (player report 2026-10-25, "why is Undo unavailable during
 *  the game?"). `--undo-left` = 10 px after the Menu button; the CSS takes the further of the two. */
function useUndoAfterMenu(ref: React.RefObject<HTMLDivElement | null>) {
  useLayoutEffect(() => {
    const bar = ref.current;
    const menu = document.getElementById('menu_button');
    if (!bar || !menu) return;
    const place = () => {
      const left = `${Math.ceil(menu.getBoundingClientRect().right - bar.getBoundingClientRect().left + 10)}px`;
      if (bar.style.getPropertyValue('--undo-left') !== left) bar.style.setProperty('--undo-left', left);
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(menu);
    ro.observe(bar);
    addEventListener('resize', place);
    return () => {
      ro.disconnect();
      removeEventListener('resize', place);
    };
  }, [ref]);
}

/** See Rank and Login as one symmetric piece (owner 2026-10-27, "P1": the join in the middle, as the legacy pair of
 *  two 140 px halves): both halves take the wider one's width, measured here (a fixed width would clip a longer
 *  language; a wrapper box would break the `#game_status > a` structure). `--pair-w` on the status bar, app.css. */
function useEqualPair(ref: React.RefObject<HTMLDivElement | null>, key: unknown) {
  useLayoutEffect(() => {
    const bar = ref.current;
    if (!bar) return;
    const fit = () => {
      bar.style.removeProperty('--pair-w');
      const a = bar.querySelector<HTMLElement>('#game_action_globalstats');
      const b = bar.querySelector<HTMLElement>('#game_action_auth:not(.logged)');
      if (!a || !b) return;
      const w = Math.ceil(Math.max(a.getBoundingClientRect().width, b.getBoundingClientRect().width));
      if (w > 0) bar.style.setProperty('--pair-w', `${w}px`);
    };
    fit();
    addEventListener('resize', fit);
    return () => removeEventListener('resize', fit);
  }, [ref, key]);
}

function StatusBar() {
  const s = useEngineState();
  // Kierki (hearts/), Makao (makao/): their own clock and score; no Undo there (owner 2026-10-26)
  const table = RULES[s.game].table;
  const hearts = useHearts();
  const makao = useMakao();
  const kulki = useKulki();
  const t58 = use358();
  const t1k = use1000();
  const ui = useUiState();
  const ctrl = useController();
  const o = ui.options.option;
  const anyLabel = o.showTime || o.showScore || o.showMoves;
  const target = env.appTarget;
  const statusRef = useRef<HTMLDivElement>(null);
  // first: the centring below measures the pair with its final widths
  useEqualPair(statusRef, `${ui.player?.player_name ?? ''}|${currentLanguage()}`);
  useCentredPair(statusRef);
  useUndoAfterMenu(statusRef);
  return (
    <>
      <div id="game_status" ref={statusRef} aria-hidden={ui.menuOpen || undefined}>
        {table && table !== 'kulki' ? null : (
          <a
            id="game_action_undo"
            href="#"
            className={(table === 'kulki' ? kulki.prev && !kulki.busy : s.history.length && !s.won) ? '' : 'inactive'}
            data-icon="undo"
            onClick={(e) => {
              e.preventDefault();
              if (table === 'kulki') kulkiStore().undo();
              else ctrl.undo();
            }}
          >
            {t('menuUndo')}
          </a>
        )}
        {target === 'offline' || target === 'multiplayer' || ui.player ? null : (
          <a
            id="game_action_globalstats"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              uiStore().toggleWindow('ranking');
            }}
          >
            {t('menuGlobalstats')}
          </a>
        )}
        {target === 'offline' ? null : ui.player ? (
          // Logged in: the flag is content (a real raster) and the greeting sits in one span the pill ellipsizes. No
          // NBSPs any more (legacy "&nbsp;&nbsp;" spaced its <img> icon): the pill's 6 px gap spaces the glyph / flag and
          // the word, the NBSPs doubled it (owner 2026-10-16: "Zaloguj" looked further from its icon than the others).
          <a
            id="game_action_auth"
            href="#"
            className="logged"
            onClick={(e) => {
              e.preventDefault();
              uiStore().toggleWindow('auth_dashboard');
            }}
          >
            <img src={sharpFlag(ui.player.flag_src)} alt="" width={16} height={11} />
            <span className="player_name">{t('menuAuthLogged', { player_name: ui.player.player_name })}</span>
          </a>
        ) : (
          // Logged out: the legacy "unknown" flag (images/flags/unknown.png) is now the user-circle glyph.
          <a
            id="game_action_auth"
            href="#"
            data-icon="unknown"
            onClick={(e) => {
              e.preventDefault();
              uiStore().toggleWindow('auth');
            }}
          >
            {t('menuAuth')}
          </a>
        )}
      </div>
      <div id="game_labels" hidden={!anyLabel || undefined}>
        {label(
          'label_time',
          'labelTime',
          formatTime(
            table === 'makao'
              ? makao.seconds
              : table === 'kulki'
                ? kulki.seconds
                : table === '358'
                ? t58.seconds
                : table === '1000'
                  ? t1k.seconds
                  : table
                    ? hearts.seconds
                    : s.seconds,
          ),
          o.showTime,
        )}
        {label(
          'label_score',
          'labelScore',
          table === 'makao'
            ? makao.game
              ? winScore(makao.game)
              : 0
            : table === 'kulki'
              ? (kulki.game?.score ?? 0)
              : table === '358'
              ? (t58.game?.scores[0] ?? 0)
              : table === '1000'
                ? (t1k.game?.scores[0] ?? 0)
                : table
                  ? (hearts.game?.scores[0] ?? 0)
                  : s.score,
          o.showScore,
        )}
        {label('label_moves', 'labelMoves', table === 'kulki' ? (kulki.game?.moves ?? 0) : s.moves, o.showMoves && (!table || table === 'kulki'))}
      </div>
    </>
  );
}

type TcfApi = (
  command: string,
  version: number,
  callback: (data: { cmpLoaded?: boolean } | boolean, ok?: boolean) => void,
) => void;
const tcfApi = (): TcfApi | undefined => (window as unknown as { __tcfapi?: TcfApi }).__tcfapi;

/** The consent window of the InMobi CMP (the head script) can be reopened from the dock (owner 2026-10-18: its
 *  floating "Privacy" button sat over the bottom right corner). True once the CMP has really loaded (not the
 *  inline stub alone: the e2e stack blocks the CMP script, ad blockers too), checked once a second for 20 s. */
function useConsentUi(): boolean {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let tries = 0;
    const id = window.setInterval(() => {
      if (++tries > 20) window.clearInterval(id);
      tcfApi()?.('ping', 2, (data) => {
        if (typeof data === 'object' && data.cmpLoaded) {
          window.clearInterval(id);
          setLoaded(true);
        }
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, []);
  // the CMP's own floating button goes while the dock has the entry (app.css)
  useEffect(() => {
    document.body.classList.toggle('consent-in-dock', loaded);
  }, [loaded]);
  return loaded;
}

function Footer({ actions }: { actions: ShellActions }) {
  const ui = useUiState();
  const dock = useDockActions(actions);
  const { sound, consentUi, paused, hasVariants, fullscreenOk } = dock;
  const dockHidden = ui.options.option.dockHidden;
  const [tip, setTip] = useState<{ text: string; x: number; y: number; right?: number | undefined } | null>(null);
  const mobile = isMobileBody();
  // data-icon: the legacy glyph image (basename without .png) the CSS mask replaces, read by the tests;
  // data-caption: the word under the glyph (the CSS draws attr(data-caption)); data-state / data-variant /
  // aria-label: read by the CSS and assistive tech.
  const button = (
    cls: string,
    langId: string,
    icon: string,
    caption: string,
    onClick: () => void,
    last = false,
    state?: 'on' | 'off',
    variant?: string,
  ) => (
    <div className={`menu_wrapper_action menu_wrapper_action_${cls.replace('menu_toggle_', '')}`}>
      <a
        href="#"
        data-lang-id={langId}
        data-icon={icon}
        data-caption={t(caption)}
        data-state={state}
        data-variant={variant}
        aria-label={t(langId)}
        className={`${cls} menu_action`}
        onClick={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement).blur(); // legacy: no focus ring after a click
          onClick();
        }}
        // The hover tip is for a real mouse only: a tap on a touch screen that is not body#mobile (an iPad
        // in portrait) fires a synthetic enter too, and the tap toast (ui/tapToast.ts) already says it.
        onPointerEnter={(e) => {
          if (mobile || e.pointerType !== 'mouse') return;
          const r = e.currentTarget.getBoundingClientRect();
          // a tile in the right half (the Privacy shield in the side layout of a low screen) opens its tip leftwards
          const right = r.left > innerWidth / 2 ? Math.max(0, innerWidth - r.right - 2) : undefined;
          setTip({ text: t(langId), x: r.left - 2, y: r.top - 36, right });
        }}
        onPointerLeave={() => setTip(null)}
      />
      <span className={last ? 'footer_separator_last' : 'footer_separator'}> | </span>
    </div>
  );
  const { toggleSound, cycleVariant, toggleFullscreen } = dock;
  return (
    <>
      <div id="footer_menu_left">
        {button(
          'menu_toggle_sound',
          'menu-bottom.sound',
          sound === 'off' ? 'sound_mute' : 'sound',
          'menuBottom.caption.sound',
          toggleSound,
          false,
          sound === 'off' ? 'off' : 'on',
        )}
        {button(
          'menu_toggle_pause',
          'menu-bottom.pause',
          paused ? 'pause_on' : 'pause_off',
          paused ? 'menuBottom.caption.paused' : 'menuBottom.caption.pause',
          actions.togglePause,
          false,
          paused ? 'on' : 'off',
        )}
        {/* Mahjong (owner 2026-10-24): no card set to change; the Help bulb takes the Cards place, so Rules stays last */}
        {dock.tiles
          ? button('menu_toggle_hint', 'menu-bottom.hint', 'hint', 'menuBottom.caption.hint', dock.hint)
          : hasVariants
            ? button(
                'menu_toggle_card_variant',
                'menu-bottom.card-variant',
                'card_variant',
                'menuBottom.caption.cards',
                cycleVariant,
                false,
                undefined,
                dock.variant,
              )
            : null}
        {fullscreenOk
          ? button(
              'menu_toggle_fullscreen',
              'menu-bottom.fullscreen',
              ui.fullscreen ? 'fullscreen_on' : 'fullscreen_off',
              'menuBottom.caption.screen',
              toggleFullscreen,
              false,
              ui.fullscreen ? 'on' : 'off',
            )
          : null}
        {button('menu_toggle_rules', 'menu-bottom.rules', 'bottom_info', 'menuBottom.caption.rules', dock.rules)}
        {/* folds the dock to this button and Privacy (owner 2026-10-23); the same switch as Options > Display */}
        {button(
          'menu_toggle_dock',
          dockHidden ? 'menu-bottom.dock-show' : 'menu-bottom.dock-hide',
          'dock',
          '',
          () => uiStore().setOption('dockHidden', !dockHidden),
          true,
        )}
      </div>
      {/* Privacy: its own tab, always in the bottom right corner of the screen (owner 2026-10-23), outside the dock
          so folding the dock or the focus mode never takes it away (the consent choices must stay reachable) */}
      {consentUi ? (
        <div id="footer_privacy">
          {button(
            'menu_toggle_privacy',
            'menu-bottom.privacy',
            'privacy',
            'menuBottom.caption.privacy',
            dock.privacy,
            true,
          )}
        </div>
      ) : null}
      {/* the theme author's line went (owner 2026-10-31); the element stays for the tests' id list (BOOT-004) */}
      <div id="footer_theme_author" hidden />
      <OpponentBar />
      <MpSearching />
      <CardNotice />
      {tip ? (
        <div
          className="tip-wrap"
          data-side={tip.right === undefined ? undefined : 'right'}
          style={tip.right === undefined ? { left: tip.x, top: tip.y } : { right: tip.right, top: tip.y }}
        >
          <div className="tip-text">{tip.text}</div>
          <div className="tip-bottom" />
        </div>
      ) : null}
    </>
  );
}

function WindowHost({ actions }: { actions: ShellActions }) {
  const ui = useUiState();
  const ctrl = useController();
  const render = (w: WindowInstance) => {
    switch (w.name) {
      case 'games':
        return <GamesWindow key={w.id} instance={w} onPlay={actions.play} />;
      case 'options':
        return <OptionsWindow key={w.id} instance={w} />;
      case 'pause':
        return <PauseWindow key={w.id} instance={w} onResume={actions.resume} />;
      case 'no_moves':
        return <NoMovesWindow key={w.id} instance={w} />;
      case 'win':
        return <WinWindow key={w.id} instance={w} onRetry={() => ctrl.retryGame()} />;
      case 'info':
        return <InfoWindow key={w.id} instance={w} />;
      case 'confirm':
        return <ConfirmWindow key={w.id} instance={w} />;
      case 'alert':
        return <AlertWindow key={w.id} instance={w} />;
      case 'opt_skin':
        return <ThemesWindow key={w.id} instance={w} />;
      case 'auth':
        return <AuthWindow key={w.id} instance={w} />;
      case 'auth_dashboard':
        return <DashboardWindow key={w.id} instance={w} />;
      case 'delete_account':
        return <DeleteAccountWindow key={w.id} instance={w} />;
      case 'ranking':
        return <RankingWindow key={w.id} instance={w} />;
      case 'flags':
        return <FlagsWindow key={w.id} instance={w} />;
      case 'multiplayer':
        return <MultiplayerWindow key={w.id} instance={w} />;
      case 'multiplayer_win':
        return <MultiplayerResultWindow key={w.id} instance={w} />;
      case 'multiplayer_leave':
        return <MultiplayerLeaveWindow key={w.id} instance={w} />;
      case 'multiplayer_done':
        return <MultiplayerDoneWindow key={w.id} instance={w} />;

      case 'hearts_score':
        return <HeartsScoreWindow key={w.id} instance={w} />;
      case 'makao_end':
        return <MakaoEndWindow key={w.id} instance={w} />;
      case 'kulki_end':
        return <KulkiEndWindow key={w.id} instance={w} />;
      case 't58_score':
        return <ScoreWindow358 key={w.id} instance={w} />;
      case 't1k_score':
        return <ScoreWindow1000 key={w.id} instance={w} />;
      default:
        return null;
    }
  };
  return <>{ui.windows.map(render)}</>;
}

export function App({ actions }: { actions: ShellActions }) {
  const ui = useUiState();
  const ctrl = useController();
  const game = useEngineState().game;
  const actionsRef = useRef(actions);
  useEffect(() => {
    actionsRef.current = actions;
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      const s = uiStore();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        if (typing || s.isOpen('pause')) return;
        e.preventDefault();
        // One undo per 500 ms from the keyboard (legacy menu.js); the Undo link is not throttled.
        const now = Date.now();
        if (now - lastUndoKeyAt < UNDO_KEY_THROTTLE_MS) return;
        lastUndoKeyAt = now;
        ctrl.undo();
        return;
      }
      if (e.key === 'Escape') {
        const top = [...s.getState().windows].reverse().find((w) => !w.closing);
        if (!top) return;
        if (top.name === 'pause') actionsRef.current.resume();
        else if (top.name !== 'win') s.closeWindow(top.name);
        return;
      }
      if ((e.key === 'p' || e.key === 'P') && !typing && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (s.isOpen('info') || s.isOpen('auth')) return;
        actionsRef.current.togglePause();
      }
    };
    const onFullscreen = () => uiStore().set({ fullscreen: !!document.fullscreenElement });
    window.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFullscreen);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFullscreen);
    };
  }, [ctrl]);
  return (
    <div
      className={`app${env.appTarget === 'multiplayer' ? ' multiplayer' : ''}`}
      data-startup={ui.startupDone ? 'done' : undefined}
      data-focus={ui.options.option.focusMode ? '' : undefined}
      data-dock-hidden={ui.options.option.dockHidden ? '' : undefined}
      data-fullscreen={ui.fullscreen ? '' : undefined}
    >
      <header className="topbar" data-menu={ui.menuOpen ? 'open' : 'closed'}>
        <Menu actions={actions} />
        <StatusBar />
      </header>
      {RULES[game].table === 'makao' ? (
        <MakaoTable />
      ) : RULES[game].table === 'kulki' ? (
        <KulkiTable />
      ) : RULES[game].table === '358' ? (
        <Table358 />
      ) : RULES[game].table === '1000' ? (
        <Table1000 />
      ) : RULES[game].table ? (
        <HeartsTable />
      ) : (
        <Board />
      )}
      <footer className="footer">
        <Footer actions={actions} />
      </footer>
      <Indicator />
      <WindowHost actions={actions} />
      <AdAnchorBand />
    </div>
  );
}
